// ============================================================================
// CONFIG SETTINGS PANEL
// ============================================================================
// Right panel: scope selector, organization/department selects, settings list,
// and a Save / Discard footer bar.
//
// SCOPE LABELS — customise these to match your domain vocabulary:
//   'SYSTEM'       → "System Defaults"       (applies to all)
//   'ORGANIZATION' → "Organization Override" (applies to a specific org/site)
//   'DEPARTMENT'   → "Department Override"   (applies to a specific team/dept)
//
// SCOPE SELECTS — the two async SyutSelects load simulated org and dept data.
// Replace loadOrganizationOptions / loadDepartmentOptions with real API calls.

import React, { useMemo, useCallback } from 'react';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import ConfigSettingField from './ConfigSettingField';
import type {
  ConfigGroup,
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigValueUpdate,
  ConfigListItem,
} from 'api/types/config.types';
import type { ScopeType } from 'hooks/api/useConfigSettings';

// --- Simulated organization options ---
// Replace with a real API call, e.g. organizationService.getAll({ searchTerm: inputValue })
const SIMULATED_ORGANIZATIONS: SelectOption[] = [
  { value: 'org-1', label: 'ORG001 - Alpha Organization' },
  { value: 'org-2', label: 'ORG002 - Beta Organization' },
  { value: 'org-3', label: 'ORG003 - Gamma Organization' },
];

// Simulated departments (in a real app, filter by selectedOrganizationId)
const SIMULATED_DEPARTMENTS: Record<string, SelectOption[]> = {
  'org-1': [
    { value: 'dept-1', label: 'Engineering' },
    { value: 'dept-2', label: 'Marketing' },
  ],
  'org-2': [
    { value: 'dept-1', label: 'Operations' },
    { value: 'dept-2', label: 'Finance' },
  ],
  'org-3': [
    { value: 'dept-1', label: 'Sales' },
  ],
};

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ============================================================================

interface ConfigSettingsPanelProps {
  activeGroup: ConfigGroup | null;
  scopeType: ScopeType;
  onScopeTypeChange: (scope: ScopeType) => void;
  selectedOrganizationId: string | null;
  onOrganizationSelect: (orgId: string | null) => void;
  selectedDepartmentId: string | null;
  onDepartmentSelect: (deptId: string | null) => void;
  effectiveValues: ConfigEffectiveValue[];
  definitions: ConfigDefinition[];
  editedValues: Record<string, ConfigValueUpdate>;
  onValueChange: (configKey: string, value?: string, listValues?: ConfigListItem[]) => void;
  onResetValue: (configKey: string) => void;
  onSave: () => void;
  onDiscard: () => void;
  hasChanges: boolean;
  saving: boolean;
  loading: boolean;
}

const ConfigSettingsPanel: React.FC<ConfigSettingsPanelProps> = ({
  activeGroup,
  scopeType,
  onScopeTypeChange,
  selectedOrganizationId,
  onOrganizationSelect,
  selectedDepartmentId,
  onDepartmentSelect,
  effectiveValues,
  definitions,
  editedValues,
  onValueChange,
  onResetValue,
  onSave,
  onDiscard,
  hasChanges,
  saving,
  loading,
}) => {
  // Async loader for organizations (replace with real API call)
  const loadOrganizationOptions = useCallback(
    async (inputValue: string): Promise<SelectOption[]> => {
      await delay(200);
      const term = inputValue.toLowerCase();
      return term
        ? SIMULATED_ORGANIZATIONS.filter((o) => o.label.toLowerCase().includes(term))
        : SIMULATED_ORGANIZATIONS;
    },
    []
  );

  // Async loader for departments filtered by selected organization
  const loadDepartmentOptions = useCallback(
    async (inputValue: string): Promise<SelectOption[]> => {
      if (!selectedOrganizationId) return [];
      await delay(150);
      const depts = SIMULATED_DEPARTMENTS[selectedOrganizationId] ?? [];
      const term = inputValue.toLowerCase();
      return term ? depts.filter((d) => d.label.toLowerCase().includes(term)) : depts;
    },
    [selectedOrganizationId]
  );

  // Show Department scope toggle only when some definitions support it
  const hasDepartmentScopeSettings = useMemo(
    () => definitions.some((d) => d.maxScopeLevel >= 2),
    [definitions]
  );

  // Sort definitions by displayOrder and pair with their effective values
  const sortedSettings = useMemo(() => {
    return definitions
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((def) => {
        const ev = effectiveValues.find((v) => v.configKey === def.configKey);
        return { definition: def, effectiveValue: ev };
      })
      .filter((item) => item.effectiveValue != null);
  }, [definitions, effectiveValues]);

  if (!activeGroup) {
    return (
      <div className="config-settings-panel">
        <div className="config-empty">Select a group to view settings</div>
      </div>
    );
  }

  return (
    <div className="config-settings-panel">
      {/* Header */}
      <div className="config-panel-header">
        <h2>{activeGroup.displayName}</h2>
        {activeGroup.description && <p>{activeGroup.description}</p>}
      </div>

      {/* Scope bar */}
      <div className="config-scope-bar">
        <div className="config-scope-toggle">
          <button
            type="button"
            className={`config-scope-toggle-btn${scopeType === 'SYSTEM' ? ' active' : ''}`}
            onClick={() => onScopeTypeChange('SYSTEM')}
          >
            System Defaults
          </button>
          <button
            type="button"
            className={`config-scope-toggle-btn${scopeType === 'ORGANIZATION' ? ' active' : ''}`}
            onClick={() => onScopeTypeChange('ORGANIZATION')}
          >
            Organization Override
          </button>
          {hasDepartmentScopeSettings && (
            <button
              type="button"
              className={`config-scope-toggle-btn${scopeType === 'DEPARTMENT' ? ' active' : ''}`}
              onClick={() => onScopeTypeChange('DEPARTMENT')}
            >
              Department Override
            </button>
          )}
        </div>

        {(scopeType === 'ORGANIZATION' || scopeType === 'DEPARTMENT') && (
          <div className="config-scope-select-row">
            <div className="config-scope-select">
              <SyutSelect
                isAsync
                loadOptions={loadOrganizationOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={selectedOrganizationId ?? undefined}
                onChange={(value: string | string[] | null) =>
                  onOrganizationSelect(typeof value === 'string' ? value : null)
                }
                placeholder="Select an organization..."
                isClearable
                size="sm"
              />
            </div>
            {scopeType === 'DEPARTMENT' && selectedOrganizationId && (
              <div className="config-scope-select">
                <SyutSelect
                  key={selectedOrganizationId}
                  isAsync
                  loadOptions={loadDepartmentOptions}
                  defaultOptions={true}
                  cacheOptions={false}
                  value={selectedDepartmentId ?? undefined}
                  onChange={(value: string | string[] | null) =>
                    onDepartmentSelect(typeof value === 'string' ? value : null)
                  }
                  placeholder="Select a department..."
                  isClearable
                  size="sm"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Settings list */}
      <div className="config-settings-list">
        {loading ? (
          <div className="config-loading">Loading settings...</div>
        ) : (scopeType === 'ORGANIZATION' || scopeType === 'DEPARTMENT') &&
          !selectedOrganizationId ? (
          <div className="config-empty">
            Select an organization to view its configuration overrides
          </div>
        ) : scopeType === 'DEPARTMENT' && selectedOrganizationId && !selectedDepartmentId ? (
          <div className="config-empty">
            Select a department to view its configuration overrides
          </div>
        ) : sortedSettings.length === 0 ? (
          <div className="config-empty">No settings found for this group</div>
        ) : (
          sortedSettings.map(({ definition, effectiveValue }) => (
            <ConfigSettingField
              key={definition.configKey}
              definition={definition}
              effectiveValue={effectiveValue!}
              editedValue={editedValues[definition.configKey]}
              onValueChange={onValueChange}
              onResetValue={onResetValue}
              scopeType={scopeType}
            />
          ))
        )}
      </div>

      {/* Footer bar — only visible when there are pending changes */}
      {(hasChanges || saving) && (
        <div className="config-footer-bar">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onDiscard}
            disabled={saving}
          >
            Discard Changes
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ConfigSettingsPanel;
